import dayjs from "dayjs";

import { getUsDateFromIsoDate } from "@components/Date";

const PLACEHOLDER_PATTERN = /\{(\w+)\}/g;

type LinkTemplates = {
  portalUrl?: string;
  signInUrl?: string;
  documentsUrl?: string;
  investmentUrl?: string;
};

type Organization = {
  name?: string;
  email?: string;
  domain?: string;
  linkTemplates?: LinkTemplates;
};

type ProjectDocument = {
  url: string;
  description: string;
};

type Project = {
  id: string;
  name?: string;
  closeDate?: string;
  documents?: ProjectDocument[];
  fundDeadlineDate?: string;
  signDeadlineDate?: string;
};

type Batch = {
  fileUrl?: string;
  isPublicFile?: boolean;
};

type TagsMap = Record<string, string>;

type Placeholders = Record<string, string>;

type Context = {
  batch?: Batch;
  project?: Project;
  tagsMap?: TagsMap;
  organization?: Organization;
};

type VariableResolver = (context: Context) => string;

type VariablesMap = Record<string, VariableResolver>;

const wrapMergeTag = (label: string): string =>
  `<span data-block="mergetag">${label}</span>`;

// The link resolvers below are a copy of the resolvers of
// api-notifications/src/helpers/templates/variablesMap.js, which is the only
// source for the links that go to the investor. Keep the two the same, so
// that the preview and the email that the service sends give the same URL.
const resolveLinkTemplate = (
  template: string,
  placeholders: Placeholders,
): string =>
  template.replace(PLACEHOLDER_PATTERN, (match: string, key: string) => {
    const value = placeholders[key];
    const hasValue = value !== undefined && value !== null;

    if (!hasValue) {
      return match;
    }

    return value;
  });

const getPortalUrl = ({ organization }: Context): string => {
  const { domain, linkTemplates = {} } = organization;
  const { portalUrl } = linkTemplates;

  const url = portalUrl || `https://${domain}`;

  return url;
};

const getSignInUrl = ({ organization }: Context): string => {
  const { domain, linkTemplates = {} } = organization;
  const { signInUrl } = linkTemplates;

  const url = signInUrl || `https://${domain}`;

  return url;
};

const getInvestmentUrl = ({ organization, project }: Context): string => {
  const { domain, linkTemplates = {} } = organization;
  const { investmentUrl } = linkTemplates;

  const defaultTemplate = `https://${domain}/investments/{projectId}/`;
  const template = investmentUrl || defaultTemplate;

  const url = resolveLinkTemplate(template, { projectId: project.id });

  return url;
};

const getDocumentsUrl = ({ organization, project }: Context): string => {
  const { domain, linkTemplates = {} } = organization;
  const { documentsUrl } = linkTemplates;

  const defaultTemplate = `https://${domain}/projects/{projectId}/documents`;
  const template = documentsUrl || defaultTemplate;

  const url = resolveLinkTemplate(template, { projectId: project.id });

  return url;
};

// The preview of a template has no batch, thus it has no file. For a file
// that is not public, the service sends this link as the documents URL, and
// most files are not public. Therefore the documents URL is the correct
// preview when there is no batch.
const getProjectFileUrl = (context: Context): string => {
  const { batch } = context;

  const documentsUrl = getDocumentsUrl(context);

  if (!batch) {
    return documentsUrl;
  }

  const fileUrl = batch.fileUrl ?? documentsUrl;
  const isPublicFile = batch.isPublicFile ?? false;

  if (!isPublicFile) {
    return documentsUrl;
  }

  return fileUrl;
};

const getProjectDocumentLinks = ({ project, tagsMap }: Context): string => {
  const { documents = [] } = project;

  const hasDocuments = documents.length > 0;

  if (!hasDocuments) {
    return wrapMergeTag(tagsMap.PROJECT_DOCUMENTS);
  }

  const documentLinks = documents.map(({ url, description }) =>
      `<a href="${url}" target="_blank">${description}</a>`).join("\n");

  return `<ul style="padding-left: 20px; margin-top: 0px; margin-bottom: 16px;">${documentLinks}</ul>`;
};

const getLastDayOfMonth = (): string => {
  const lastDayOfMonth = dayjs().endOf("month");

  return getUsDateFromIsoDate(lastDayOfMonth);
};

const getFirstDistributionDate = (): string => {
  const firstDistributionDate = dayjs()
    .startOf("month")
    .add(2, "month")
    .add(4, "day");

  return getUsDateFromIsoDate(firstDistributionDate);
};

const commonVariablesMap: VariablesMap = {
  PORTAL_URL: getPortalUrl,
  SIGNIN_URL: getSignInUrl,
  ORGANIZATION_NAME: ({ organization }) => organization.name,
  ORGANIZATION_EMAIL: ({ organization }) => organization.email,
  MONTH_LAST_DAY: getLastDayOfMonth,
  DISTRIBUTION_START_DATE: getFirstDistributionDate,
};

const getProjectVariablesMap = (tagsMap: TagsMap): VariablesMap => {
  return {
    INVESTMENT_URL: getInvestmentUrl,
    PROJECT_NAME: ({ project: { name } }) => name,
    SIGN_DEADLINE: ({ project: { signDeadlineDate } }) =>
      signDeadlineDate
        ? getUsDateFromIsoDate(signDeadlineDate)
        : wrapMergeTag(tagsMap.SIGN_DEADLINE),
    FUND_DEADLINE: ({ project: { fundDeadlineDate } }) =>
      fundDeadlineDate
        ? getUsDateFromIsoDate(fundDeadlineDate)
        : wrapMergeTag(tagsMap.FUND_DEADLINE),
    PROJECT_DOCUMENTS: getProjectDocumentLinks,
    PROJECT_CLOSE_DATE: ({ project: { closeDate } }) =>
      closeDate
        ? getUsDateFromIsoDate(closeDate)
        : wrapMergeTag(tagsMap.PROJECT_CLOSE_DATE),
    INVESTOR_FILE_URL: getDocumentsUrl,
    PROJECT_FILE_URL: getProjectFileUrl,
  };
};

const renderContent = (
  template = "",
  context: Context = {},
  isSubject = false,
  variables: string[] = [],
): string | undefined => {
  if (!template) {
    return;
  }

  let result = template;

  let variablesMap: VariablesMap = {
    ...commonVariablesMap,
  };

  const { project, tagsMap } = context;

  if (project) {
    const projectVariablesMap = getProjectVariablesMap(tagsMap);
    variablesMap = { ...variablesMap, ...projectVariablesMap };
  }

  for (const variable of variables) {
    const [ variableName, replacement ] = variable.split(":");

    let value = `<span data-block="mergetag">${replacement}</span>`;

    const valueResolver = variablesMap[variableName];

    if (valueResolver) {
      value = valueResolver(context);
    }

    const mergeTagRegex = isSubject
      ? new RegExp(`\\%${variableName}\\%`, "g")
      : new RegExp(`<span data-block="mergetag">${replacement}</span>`, "g");

    result = result.replace(mergeTagRegex, value);
  }

  const tagKeys = Object.keys(tagsMap);

  for (const key of tagKeys) {
    let tag = tagsMap[key];

    const isUrl = key.endsWith("_URL");
    const valueResolver = variablesMap[key];

    if (valueResolver) {
      tag = valueResolver(context);

    } else if (isUrl) {
      tag = `%${key}%`;

    }

    const regex = new RegExp(`\\%${key}\\%`, "g");

    result = isSubject
      ? result.replace(regex, `<span data-block="mergetag">${tag}</span>`)
      : result.replace(regex, tag);
  }

  return result;
};

export default renderContent;
